<template>
  <div>
    <v-row dense>
      <v-col>
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            v-model="dirtySetParams.NMRP"
            outlined
            dense
            :label="$t('training_builder.text_input.num_of_repetitions.title')"
            :placeholder="$t('label.e_g') + ' ' + 10"
            :decimal-precision="0"
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
      <v-col>
        <minute-second-input
          :value="dirtySetParams.RPWRDR"
          :label="$t('training_builder.text_input.rep_work_period.title')"
          variable="RPWRDR"
          @input="handleDurationInput"
        />
      </v-col>
      <v-col>
        <minute-second-input
          :value="dirtySetParams.RPRSDR"
          :label="$t('training_builder.text_input.rep_rest_period.title')"
          variable="RPRSDR"
          @input="handleDurationInput"
        />
      </v-col>
    </v-row>

    <v-divider class="mb-6" />

    <v-row dense>
      <v-col cols="6">
        <minute-second-input
          :value="dirtySetParams.RSSTDR"
          :label="$t('training_builder.text_input.rest_time_after_set')"
          variable="RSSTDR"
          @input="handleDurationInput"
        />
      </v-col>
    </v-row>

    <v-divider class="mb-6" />

    <v-row
      dense
      align="end"
    >
      <v-col>
        <kilometer-meter-input
          :value="dirtySetParams.DIST"
          variable="DIST"
          :label="$t('table_headers.performance.player_details.efforts.distance')"
          @input="dirtySetParams.DIST = $event"
        />
      </v-col>
      <v-col>
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            :value="displayedSPD"
            outlined
            dense
            :label="$t('training_builder.text_input.speed.title')"
            :placeholder="$t('label.e_g') + ' ' + 10"
            :suffix="$t(`suffixes.${getUnitSuffixForUser('km/h')}`)"
            :decimal-precision="1"
            :error-messages="errors"
            @input="displayedSPD = $event"
          />
        </validation-provider>
      </v-col>
    </v-row>

    <v-divider class="mb-6" />

    <v-row dense>
      <v-col>
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            v-model="dirtySetParams.NUMCOD"
            outlined
            dense
            :label="$t('training_builder.text_input.num_of_cod.title')"
            :placeholder="$t('label.e_g') + ' ' + 10"
            :decimal-precision="0"
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
      <v-col>
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            v-model="dirtySetParams.DEGCOD"
            outlined
            dense
            :label="$t('training_builder.text_input.degree_of_cod')"
            :placeholder="$t('label.e_g') + ' ' + 5"
            suffix="deg"
            :decimal-precision="1"
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
    </v-row>

    <v-divider class="mb-6" />

    <v-row dense>
      <v-col cols="6">
        <div class="text-body-1 mb-2">
          {{ $t('training_builder.dropdown.terrain.title') }}
        </div>
        <validation-provider
          v-slot="{ errors }"
        >
          <v-select
            v-model="dirtySetParams.TRRN"
            outlined
            dense
            :placeholder="$t('training_builder.dropdown.terrain.placeholder')"
            :error-messages="errors"
            :items="[ { text: $t('training_builder.dropdown.terrain.item.grass'), value: 'Grass' }, { text: $t('training_builder.dropdown.terrain.item.artificial_grass'), value: 'Artificial grass' }, { text: $t('training_builder.dropdown.terrain.item.hybrid_grass'), value: 'Hybrid grass' }, { text: $t('training_builder.dropdown.terrain.item.tartan'), value: 'Tartan' }, { text: $t('training_builder.dropdown.terrain.item.clay'), value: 'Clay' }, { text: $t('training_builder.dropdown.terrain.item.parquet'), value: 'Parquet' }, { text: $t('training_builder.dropdown.terrain.item.concrete'), value: 'Concrete' }, { text: $t('training_builder.dropdown.terrain.item.tatami'), value: 'Tatami' }, { text: $t('training_builder.dropdown.terrain.item.ice'), value: 'Ice' }, { text: $t('training_builder.dropdown.terrain.item.pool'), value: 'Pool' }, { text: $t('training_builder.dropdown.terrain.item.sand'), value: 'Sand' }, ]"
          />
        </validation-provider>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import NumberInput from '@/components/helpers/NumberInput.vue'
import { cloneVariables, convertSpeedByUnitSystem } from '@/components/training-builder/helpers'
import MinuteSecondInput from '@/components/helpers/MinuteSecondInput.vue'
import KilometerMeterInput from '@/components/helpers/KilometerMeterInput.vue'
import { getUnitSuffixForUser } from '@/utils/helpers'
import { cloneDeep } from 'lodash'

export default {
  name: 'SpeedParamsEntry',
  components: { KilometerMeterInput, MinuteSecondInput, NumberInput },
  props: {
    exercise: {
      type: Object,
      required: true
    },
    setParams: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dirtySetParams: cloneVariables(this.setParams),
      internalSPD: null
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    displayedSPD: {
      get() {
        return convertSpeedByUnitSystem(this.internalSPD, this.user.organisation_settings.unit_system, true)
      },
      set(value) {
        this.dirtySetParams.SPD = convertSpeedByUnitSystem(value, this.user.organisation_settings.unit_system, false)
      }
    }
  },
  watch: {
    dirtySetParams: {
      deep: true,
      handler: function (newValue) {
        this.$emit('update:setParams', newValue)
      }
    }
  },
  mounted() {
    this.internalSPD = cloneDeep(this.setParams.SPD)
  },
  methods: {
    getUnitSuffixForUser,
    checkIfParamExists(param) {
      if (!this.exercise.tags) return false

      return this.exercise.tags.some(t => t.tag_id === 'params' && t.value === param)
    },
    handleDurationInput(min, sec, variable) {
      this.dirtySetParams[variable] = min * 60 + sec > 0 ? min * 60 + sec : null
    }
  }
}
</script>
